/**
 * Payer-specific appeal letter templates
 * 
 * These templates encode the "Gold Standard" appeal architecture derived from
 * successful appeal letters, with payer-specific variations for AARP/Medicare Advantage,
 * BCBS, UHC/Optum, and a generic fallback.
 */

/** Category of denial for determining appeal strategy */
export type DenialCategory = 
  | 'Administrative'
  | 'Authorization' 
  | 'Clinical'
  | 'SiteOfCare'
  | 'CodingBilling'
  | 'TimelyCoverage'
  | 'Other';

/** Procedure category for evidence prioritization */
export type ProcedureCategory = 
  | 'Spine'
  | 'Nerve'
  | 'Arthroscopy'
  | 'General';

export type PayerTemplateId = 
  | 'AARP_MEDICARE_ADV'
  | 'BCBS'
  | 'UHC_OPTUM'
  | 'GENERIC';

export interface LetterSection {
  id: string;
  name: string;
  description: string;
  required: boolean;
  /** Conditions under which this section is required (if not always required) */
  requiredWhen?: {
    denialCategories?: DenialCategory[];
    procedureCategories?: ProcedureCategory[];
    payers?: PayerTemplateId[];
  };
}

export interface PayerTemplate {
  id: PayerTemplateId;
  name: string;
  /** Regex patterns to match payer names */
  payerPatterns: RegExp[];
  /** Sections that are always required for this payer */
  requiredSections: string[];
  /** Sections that are optional but recommended */
  optionalSections: string[];
  /** Payer-specific closing language */
  closingLanguage: string;
  /** Tone and style guidance */
  toneGuidance: string;
  /** Special considerations for this payer */
  specialConsiderations: string[];
  /** Multi-denial framing guidance */
  multiDenialGuidance: string;
}

/**
 * Standard letter sections following the Gold Standard architecture
 */
export const LETTER_SECTIONS: Record<string, LetterSection> = {
  header: {
    id: 'header',
    name: 'Header',
    description: 'Payer address, claim identifiers, patient info, DOS, procedure statement',
    required: true,
  },
  denialFraming: {
    id: 'denialFraming',
    name: 'Denial Framing',
    description: 'Acknowledge denial reason and reframe toward medical necessity or administrative error',
    required: true,
  },
  clinicalHistory: {
    id: 'clinicalHistory',
    name: 'Clinical History',
    description: 'Age, diagnosis, pain severity, symptom onset, functional impact, relevant comorbidities',
    required: true,
  },
  physicalExam: {
    id: 'physicalExam',
    name: 'Physical Examination / Diagnostic Findings',
    description: 'Key exam findings, imaging/EMG results with dates and clinically relevant findings',
    required: true,
  },
  failedConservative: {
    id: 'failedConservative',
    name: 'Failed Conservative Management',
    description: 'List of non-surgical treatments attempted: PT, bracing, medications, injections, activity modification',
    required: true,
    requiredWhen: {
      // Not required for emergency procedures
    },
  },
  procedureDetails: {
    id: 'procedureDetails',
    name: 'Procedure Details / Clinical Rationale',
    description: 'What was done, why it was necessary, intraoperative findings if applicable',
    required: true,
  },
  postOpCourse: {
    id: 'postOpCourse',
    name: 'Post-Operative Course',
    description: 'POD-by-POD clinical needs justifying site of care (for inpatient/site-of-care disputes)',
    required: false,
    requiredWhen: {
      denialCategories: ['SiteOfCare', 'Administrative'],
      payers: ['UHC_OPTUM', 'AARP_MEDICARE_ADV'],
    },
  },
  medicalNecessityChecklist: {
    id: 'medicalNecessityChecklist',
    name: 'Medical Necessity Established By',
    description: 'Numbered list of 5 justification points',
    required: true,
  },
  administrativeConsiderations: {
    id: 'administrativeConsiderations',
    name: 'Administrative Considerations',
    description: 'Good faith reliance on authorization, waiver/estoppel arguments',
    required: false,
    requiredWhen: {
      denialCategories: ['Administrative', 'Authorization', 'SiteOfCare'],
    },
  },
  closingRequest: {
    id: 'closingRequest',
    name: 'Request for Reconsideration',
    description: 'Professional request for approval based on medical necessity',
    required: true,
  },
};

/**
 * The 5-point Medical Necessity checklist (verbatim from your templates)
 */
export const MEDICAL_NECESSITY_CHECKLIST = [
  'Persistent, function-limiting symptoms despite appropriate conservative care',
  'Objective examination and diagnostic evidence supporting the diagnosis',
  'Clear correlation between clinical findings and the procedure performed',
  'Appropriateness of the selected treatment based on accepted standards of care',
  'Intraoperative findings validating surgical decision-making (if applicable)',
];

/**
 * AARP Medicare Advantage template
 * Emphasis on post-op course for site-of-care disputes
 */
export const AARP_MEDICARE_ADV_TEMPLATE: PayerTemplate = {
  id: 'AARP_MEDICARE_ADV',
  name: 'AARP Medicare Advantage',
  payerPatterns: [
    /aarp/i,
    /medicare\s*adv/i,
    /united\s*health.*medicare/i,
    /uhc.*medicare/i,
  ],
  requiredSections: [
    'header',
    'denialFraming',
    'clinicalHistory',
    'physicalExam',
    'failedConservative',
    'procedureDetails',
    'medicalNecessityChecklist',
    'closingRequest',
  ],
  optionalSections: [
    'postOpCourse',
    'administrativeConsiderations',
  ],
  closingLanguage: `We kindly ask that you consider these circumstances and approve coverage for this claim based on medical necessity. The physician's decision was made to ensure the best possible care and outcome for the patient, which aligns with your company's commitment to quality healthcare.

We appreciate your review of this appeal and look forward to a favorable response.`,
  toneGuidance: 'Professional and respectful. Emphasize medical necessity first, administrative waiver second. Include post-op course details to justify inpatient status when applicable.',
  specialConsiderations: [
    'When site-of-care is disputed, document post-op complexity in detail',
    'Reference Medicare coverage criteria when available',
    'Include strong clinical narrative supporting necessity',
  ],
  multiDenialGuidance: 'Address each denial reason in sequence, but unify under the medical necessity argument. Start with the primary denial, then address secondary issues.',
};

/**
 * Blue Cross Blue Shield template
 * Emphasis on site-of-care/authorization considerations
 */
export const BCBS_TEMPLATE: PayerTemplate = {
  id: 'BCBS',
  name: 'Blue Cross Blue Shield',
  payerPatterns: [
    /bcbs/i,
    /blue\s*cross/i,
    /blue\s*shield/i,
    /anthem/i,
    /carefirst/i,
    /highmark/i,
    /horizon/i,
    /premera/i,
    /regence/i,
  ],
  requiredSections: [
    'header',
    'denialFraming',
    'clinicalHistory',
    'physicalExam',
    'failedConservative',
    'procedureDetails',
    'medicalNecessityChecklist',
    'closingRequest',
  ],
  optionalSections: [
    'postOpCourse',
    'administrativeConsiderations',
  ],
  closingLanguage: `Based on the above, we respectfully request prompt reconsideration and approval of this claim. The medical record fully supports both the medical necessity of the procedure and the appropriateness of the site of care.

Thank you for your review.`,
  toneGuidance: 'Combine contract/network status arguments with medical necessity. When BCBS raises administrative issues, never drop the clinical case. Clear conservative failure documentation is critical.',
  specialConsiderations: [
    'Always pair administrative arguments with clinical evidence',
    'Document that BCBS was aware of facility and procedure during authorization',
    'Emphasize good faith reliance on verification process',
    'Include objective EMG findings for nerve procedures',
  ],
  multiDenialGuidance: 'For BCBS, address network/administrative issues first to clear procedural barriers, then reinforce with clinical necessity. Present as complementary arguments.',
};

/**
 * UnitedHealthcare / Optum template
 * Critical emphasis on post-op course for site-of-care disputes
 */
export const UHC_OPTUM_TEMPLATE: PayerTemplate = {
  id: 'UHC_OPTUM',
  name: 'UnitedHealthcare / Optum',
  payerPatterns: [
    /united\s*health/i,
    /uhc(?!\s*medicare)/i,
    /optum/i,
    /oxford/i,
  ],
  requiredSections: [
    'header',
    'denialFraming',
    'clinicalHistory',
    'physicalExam',
    'failedConservative',
    'procedureDetails',
    'medicalNecessityChecklist',
    'closingRequest',
  ],
  optionalSections: [
    'postOpCourse',
    'administrativeConsiderations',
  ],
  closingLanguage: `We respectfully request prompt reconsideration and approval of this claim based on the clinical evidence and UHC's medical necessity standards.

Thank you for your thoughtful review.`,
  toneGuidance: 'Reference UHC medical necessity standards explicitly. Post-op course section is critical for site-of-care disputes. Be specific about diagnosis-procedure correlation.',
  specialConsiderations: [
    'Post-operative course is CRITICAL for site-of-care disputes',
    'Reference UHC published coverage standards when possible',
    'Address "diagnosis inconsistent with procedure" denials with clear clinical correlation',
    'Document POD-by-POD clinical needs in detail',
  ],
  multiDenialGuidance: 'For UHC, lead with clinical necessity argument, addressing each denial code with specific evidence. Reference UHC medical policy where applicable.',
};

/**
 * Generic template for unrecognized payers
 */
export const GENERIC_TEMPLATE: PayerTemplate = {
  id: 'GENERIC',
  name: 'Generic Appeal Template',
  payerPatterns: [],
  requiredSections: [
    'header',
    'denialFraming',
    'clinicalHistory',
    'physicalExam',
    'failedConservative',
    'procedureDetails',
    'medicalNecessityChecklist',
    'closingRequest',
  ],
  optionalSections: [
    'postOpCourse',
    'administrativeConsiderations',
  ],
  closingLanguage: `Based on the above, we respectfully request prompt reconsideration and approval of this claim. The medical record fully supports the necessity and appropriateness of the care provided, which was rendered in the best interest of the patient and consistent with high-quality, evidence-based practice.

Thank you for your time and thoughtful review.`,
  toneGuidance: 'Professional and factual. Balance clinical evidence with policy compliance. Maintain respectful but firm tone throughout.',
  specialConsiderations: [
    'Include all standard sections for comprehensive coverage',
    'Adapt language based on specific denial reason',
    'Include administrative considerations when authorization issues are present',
  ],
  multiDenialGuidance: 'Address denial reasons in order of financial impact, unifying all under the medical necessity framework.',
};

/**
 * All available payer templates
 */
export const PAYER_TEMPLATES: PayerTemplate[] = [
  AARP_MEDICARE_ADV_TEMPLATE,
  BCBS_TEMPLATE,
  UHC_OPTUM_TEMPLATE,
  GENERIC_TEMPLATE,
];

/**
 * Detect the appropriate payer template based on payer name
 */
export function detectPayerTemplate(payerName: string): PayerTemplate {
  for (const template of PAYER_TEMPLATES) {
    if (template.payerPatterns.some(pattern => pattern.test(payerName))) {
      return template;
    }
  }
  return GENERIC_TEMPLATE;
}

/**
 * Determine which sections are required based on denial category, procedure, and payer
 */
export function getRequiredSections(
  template: PayerTemplate,
  denialCategory: DenialCategory,
  procedureCategory: ProcedureCategory
): string[] {
  const required = new Set(template.requiredSections);

  // Check optional sections for conditional requirements
  for (const sectionId of template.optionalSections) {
    const section = LETTER_SECTIONS[sectionId];
    if (!section?.requiredWhen) continue;

    const { denialCategories, procedureCategories, payers } = section.requiredWhen;

    const matchesDenial = !denialCategories || denialCategories.includes(denialCategory);
    const matchesProcedure = !procedureCategories || procedureCategories.includes(procedureCategory);
    const matchesPayer = !payers || payers.includes(template.id);

    if (matchesDenial && matchesProcedure && matchesPayer) {
      required.add(sectionId);
    }
  }

  return Array.from(required);
}

/**
 * Section order for letter generation (Gold Standard architecture)
 */
export const SECTION_ORDER = [
  'header',
  'denialFraming',
  'clinicalHistory',
  'physicalExam',
  'failedConservative',
  'procedureDetails',
  'postOpCourse',
  'medicalNecessityChecklist',
  'administrativeConsiderations',
  'closingRequest',
];
